import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TokenService } from '@core/auth/services/token.service';
import { ContextService } from '@core/context/services/context.service';
import { SKIP_ERROR_NOTIFICATION } from '@core/error-handling/services/http-error-notification.interceptor';
import { environment } from '@environments/environment';

export interface InstrumentoConfiguracaoEstabelecimento {
  permiteUtilizarParecer?: boolean;
  permiteUtilizarCompetencias?: boolean;
  permiteUtilizarConhecimentosConteudos?: boolean;
  permiteUtilizarHabilidadesCapacidades?: boolean;
  permiteUtilizarAtitudes?: boolean;
  permiteRecuperacaoParalela?: boolean;
  permiteRecuperacaoPeriodoAvaliativo?: boolean;
  permiteNotaComplementar?: boolean;
}

export interface InstrumentoPeriodoOption {
  id?: number;
  descricao?: string;
  dataInicial?: string;
  dataFinal?: string;
  modoAvaliacao?: string;
  tipoPeriodo?: string;
  periodoAvaliativo?: {
    id: number;
    descricao?: string;
    dataInicial?: string;
    dataFinal?: string;
  };
  [key: string]: unknown;
}

export interface InstrumentoPublicavel {
  publicado?: boolean;
  dataPublicacao?: string | null;
}

export interface InstrumentoAvaliacaoRegistro {
  id?: number;
  version?: number | null;
  versao?: number | null;
  descricao?: string;
  sigla?: string;
  data?: string;
  tipoInstrumento?: string;
  tipoPeriodo?: string;
  modoAvaliacao?: string;
  conteudo?: string | null;
  criteriosAvaliativos?: string | null;
  notaMinima?: number | null;
  notaMaxima?: number | null;
  peso?: number | null;
  turma?: { id: number; descricao?: string; tipoAtendimento?: string; etapaMatriz?: { modular?: boolean } };
  itemEducacional?: { id: number; descricao?: string; isCampoExperiencia?: boolean };
  periodoAvaliativo?: { id: number; descricao?: string; dataInicial?: string; dataFinal?: string } | null;
  instrumentoRecuperacao?: InstrumentoAvaliacaoRegistro | null;
  publicavel?: InstrumentoPublicavel;
  publicado?: boolean;
  dataPublicacao?: string | null;
  notaMinimaConfiguracao?: number;
  notaMaximaConfiguracao?: number;
  quantidadeDecimais?: string;
  decimaisAvaliacao?: string;
  [key: string]: unknown;
}

export interface InstrumentoArquivoAnexo {
  id?: number | string;
  nome?: string;
  tipo?: string;
  tamanho?: number;
  arquivo?: string;
  origem?: string;
  pasta?: string;
  chave?: string;
  publicado?: boolean;
  [key: string]: unknown;
}

export interface InstrumentoTurmaContexto {
  id: number;
  descricao?: string;
  nome?: string;
  estabelecimento?: {
    id: number;
    nome?: string;
  };
  tipoAtendimento?: string;
  etapaMatriz?: {
    modular?: boolean;
  };
  [key: string]: unknown;
}

export interface InstrumentoItemEducacionalOption {
  id: number;
  descricao?: string;
  nome?: string;
  isCampoExperiencia?: boolean;
  etapaMatrizDisciplina?: {
    id: number;
  };
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class InstrumentoAvaliacaoService {
  private readonly apiUrl = environment.apiUrl;
  private readonly arquivosUrl = this.buildArquivosUrl();
  private readonly quietContext = new HttpContext().set(SKIP_ERROR_NOTIFICATION, true);

  constructor(
    private http: HttpClient,
    private tokenService: TokenService,
    private contextService: ContextService
  ) {}

  private buildArquivosUrl(): string {
    if (this.apiUrl.endsWith('/api')) {
      return `${this.apiUrl.slice(0, -4)}/zuul/api/commons/arquivos`;
    }

    return `${this.apiUrl}/commons/arquivos`;
  }

  get(id: number): Observable<InstrumentoAvaliacaoRegistro> {
    return this.http.get<InstrumentoAvaliacaoRegistro>(
      `${this.apiUrl}/professores/instrumento-avaliacao/${id}`,
      { context: this.quietContext }
    );
  }

  list(params: { filter?: string; offset?: number; limit?: number; sort?: string }): Observable<{ content: InstrumentoAvaliacaoRegistro[]; totalElements?: number }> {
    let httpParams = new HttpParams()
      .set('offset', String(params.offset ?? 0))
      .set('limit', String(params.limit ?? 999));

    if (params.filter?.trim()) {
      httpParams = httpParams.set('filter', params.filter.trim());
    }

    if (params.sort) {
      httpParams = httpParams.set('sort', params.sort);
    }

    return this.http.get<{ content: InstrumentoAvaliacaoRegistro[]; totalElements?: number }>(
      `${this.apiUrl}/professores/instrumento-avaliacao`,
      {
        params: httpParams,
        context: this.quietContext
      }
    );
  }

  /** Busca via endpoint commons (paridade com o WEB AngularJS); retorna apenas tipos INSTRUMENTO/CALCULO_INSTRUMENTOS. */
  listPorContexto(params: {
    turmaId: number;
    periodoAvaliativoId?: number | null;
    itemEducacionalId?: number | null;
  }): Observable<{ content: InstrumentoAvaliacaoRegistro[]; totalElements?: number }> {
    let httpParams = new HttpParams().set('turma', String(params.turmaId));

    if (params.periodoAvaliativoId) {
      httpParams = httpParams.set('periodoAvaliativo', String(params.periodoAvaliativoId));
    }

    if (params.itemEducacionalId) {
      httpParams = httpParams.set('itemEducacional', String(params.itemEducacionalId));
    }

    return this.http.get<{ itensAvaliaveis?: InstrumentoAvaliacaoRegistro[] }>(
      `${this.apiUrl}/commons/instrumento-avaliacao`,
      { params: httpParams, context: this.quietContext }
    ).pipe(
      map((response) => {
        const content = (response?.itensAvaliaveis || []).filter((item) => {
          const tipoPeriodo = String((item as Record<string, unknown>)['tipoPeriodo'] || '').toUpperCase();
          return tipoPeriodo === 'INSTRUMENTO' || tipoPeriodo === 'CALCULO_INSTRUMENTOS';
        });
        return { content, totalElements: content.length };
      })
    );
  }

  save(payload: InstrumentoAvaliacaoRegistro): Observable<InstrumentoAvaliacaoRegistro> {
    if (payload.id) {
      return this.http.put<InstrumentoAvaliacaoRegistro>(
        `${this.apiUrl}/professores/instrumento-avaliacao/${payload.id}`,
        payload
      );
    }

    return this.http.post<InstrumentoAvaliacaoRegistro>(
      `${this.apiUrl}/professores/instrumento-avaliacao`,
      payload
    );
  }

  updatePeriodoOrDate(payload: InstrumentoAvaliacaoRegistro): Observable<InstrumentoAvaliacaoRegistro> {
    return this.http.put<InstrumentoAvaliacaoRegistro>(
      `${this.apiUrl}/professores/instrumento-avaliacao/alterar-periodo-instrumento/${payload.id}`,
      payload
    );
  }

  remove(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/professores/instrumento-avaliacao/${id}`);
  }

  publicar(turmaId: number, id: number): Observable<InstrumentoAvaliacaoRegistro> {
    return this.http.put<InstrumentoAvaliacaoRegistro>(
      `${this.apiUrl}/commons/turma/${turmaId}/avaliacoes/publicar/${id}`,
      {}
    );
  }

  getConfiguracaoEstabelecimento(turmaId: number): Observable<InstrumentoConfiguracaoEstabelecimento> {
    return this.http.get<InstrumentoConfiguracaoEstabelecimento>(
      `${this.apiUrl}/commons/configuracoes-estabelecimentos/${turmaId}`,
      { context: this.quietContext }
    );
  }

  getDescricoesRecentes(limit = 5): Observable<Array<{ descricao: string }>> {
    return this.http.get<Array<{ descricao: string }>>(
      `${this.apiUrl}/professores/instrumento-avaliacao/descricoes-recentes`,
      {
        params: new HttpParams().set('limit', String(limit)),
        context: this.quietContext
      }
    );
  }

  getPeriodosItemAvaliacao(itemEducacionalId: number, existsModoAvaliacao = true): Observable<InstrumentoPeriodoOption[]> {
    let params = new HttpParams();

    if (existsModoAvaliacao) {
      params = params.set('filter', 'modoAvaliacao is not null');
    }

    return this.http.get<InstrumentoPeriodoOption[]>(
      `${this.apiUrl}/professores/item-educacional/${itemEducacionalId}/item-avaliacao-turma`,
      {
        params,
        context: this.quietContext
      }
    );
  }

  getTurmasContexto(
    filter = '(modular != true or modular is null)',
    sort = 'descricao',
    limit = 999
  ): Observable<{ content: InstrumentoTurmaContexto[]; totalElements?: number }> {
    return this.http.get<{ content: InstrumentoTurmaContexto[]; totalElements?: number }>(
      `${this.apiUrl}/commons/turma`,
      {
        params: new HttpParams()
          .set('filter', filter)
          .set('sort', sort)
          .set('offset', '0')
          .set('limit', String(limit)),
        context: this.quietContext
      }
    );
  }

  getItensTurma(turmaId: number): Observable<InstrumentoItemEducacionalOption[]> {
    return this.http.get<InstrumentoItemEducacionalOption[]>(
      `${this.apiUrl}/commons/turma/${turmaId}/itens-educacionais`,
      { context: this.quietContext }
    );
  }

  getCampoAdicional(id: number): Observable<Record<string, unknown> | null> {
    return this.http.get<Record<string, unknown> | null>(
      `${this.apiUrl}/professores/instrumento-avaliacao/campos-adicionais/${id}`,
      { context: this.quietContext }
    );
  }

  /** Busca o template de campos adicionais sem necessidade de ID (usado na criação). */
  getCamposAdicionaisTemplate(): Observable<Record<string, unknown> | null> {
    return this.http.get<Record<string, unknown> | null>(
      `${this.apiUrl}/professores/instrumento-avaliacao/campos-adicionais`,
      { context: this.quietContext }
    );
  }

  uploadAnexo(file: File): Observable<InstrumentoArquivoAnexo> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<InstrumentoArquivoAnexo>(this.arquivosUrl, formData);
  }

  getDownloadAnexoUrl(id: number | string): string | null {
    const token = this.tokenService.getAccessToken();
    const userAccess = this.contextService.getUserAccess();

    if (!token || !userAccess) {
      return null;
    }

    const params = new URLSearchParams();
    params.set('access_token', token);
    params.set('user_access', userAccess);

    return `${this.arquivosUrl}/${id}/download?${params.toString()}`;
  }

  /** Inclui dataInicio/dataFim; usado para criar período sintético em turmas de atividade e EJA modular. */
  getItemEducacional(itemEducacionalId: number): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/professores/item-educacional/${itemEducacionalId}`,
      { context: this.quietContext }
    );
  }
}
